import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KES } from "@/lib/format";
import { getChartTheme } from "@/lib/chartTheme";
import { useAdminTheme } from "@/context/AdminThemeContext";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function formatTick(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
}

const AGING_ACCENT = { "0-7": "text-nhp-info", "8-14": "text-nhp-warning", "15-30": "text-nhp-warning", "30+": "text-nhp-critical" };

export default function PppoeReports() {
  const [revenue, setRevenue] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [aging, setAging] = useState(null);
  const [from, setFrom] = useState(isoDaysAgo(29));
  const [to, setTo] = useState(isoDaysAgo(0));
  const { theme } = useAdminTheme();
  const chart = getChartTheme(theme);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pppoe/reports/revenue", { params: { from, to } });
        setRevenue(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, [from, to]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pppoe/reports/plan-distribution");
        setDistribution(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pppoe/reports/aging");
        setAging(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    })();
  }, []);

  const worstBucket = aging?.buckets ? [...aging.buckets].reverse().find((b) => b.count > 0) : null;

  return (
    <div>
      <PageHeader testId="pppoe-reports-header" title="PPPoE Reports" subtitle="Revenue, plan mix, and overdue aging" />

      <div className="p-4 md:p-8 space-y-6">
        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// REVENUE</div>
              <div className="font-heading font-bold text-xl mt-1">Daily Revenue</div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 font-mono text-xs">
                <input
                  type="date" data-testid="pppoe-reports-revenue-from" value={from} max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
                <span className="text-nhp-muted">to</span>
                <input
                  type="date" data-testid="pppoe-reports-revenue-to" value={to} min={from} max={isoDaysAgo(0)}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 px-2 bg-nhp-bg border border-nhp-border text-nhp-text rounded-none"
                />
              </div>
              {revenue && (
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Total</div>
                  <div className="font-mono font-bold text-2xl text-nhp-primary tabular-nums">
                    {KES(revenue.series.reduce((a, b) => a + b.revenue_kes, 0))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {revenue && (
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={revenue.series}>
                <defs>
                  <linearGradient id="pppoe-rev-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chart.accent} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={chart.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                <XAxis dataKey="date" tickFormatter={formatTick} tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <Tooltip
                  contentStyle={chart.tooltip}
                  labelStyle={chart.tooltipLabel} itemStyle={chart.tooltipItem}
                  labelFormatter={formatTick}
                  formatter={(v) => KES(v)}
                />
                <Area type="monotone" dataKey="revenue_kes" stroke={chart.accent} strokeWidth={2} fill="url(#pppoe-rev-fill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// PLAN MIX</div>
          <div className="font-heading font-bold text-xl mt-1 mb-4">Accounts by Plan</div>
          {distribution && distribution.items.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={distribution.items}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.gridStroke} vertical={false} />
                <XAxis dataKey="plan_name" tick={chart.tickLg} axisLine={chart.axisLine} tickLine={false} />
                <YAxis tick={chart.tick} axisLine={chart.axisLine} tickLine={false} />
                <Tooltip
                  contentStyle={chart.tooltip}
                  cursor={chart.tooltipCursor}
                  formatter={(v, name) => (name === "account_count" ? v : KES(v))}
                />
                <Bar dataKey="account_count" name="Accounts" fill={chart.accent} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-nhp-muted">No accounts yet.</div>
          )}
        </div>

        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">// AGING</div>
          <div className="font-heading font-bold text-xl mt-1 mb-4">Overdue Balances</div>
          {aging && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {aging.buckets.map((b) => (
                  <StatCard
                    key={b.label}
                    testId={`pppoe-aging-${b.label}`}
                    label={`${b.label} days`}
                    value={KES(b.amount_kes)}
                    sublabel={`${b.count} account${b.count === 1 ? "" : "s"}`}
                    accent={AGING_ACCENT[b.label] || "text-nhp-muted"}
                  />
                ))}
              </div>
              {worstBucket ? (
                <div className="border border-nhp-border">
                  <div className="hidden md:grid grid-cols-12 px-4 py-2 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
                    <div className="col-span-4">Username</div>
                    <div className="col-span-4">Amount</div>
                    <div className="col-span-4">Days overdue</div>
                  </div>
                  <div className="divide-y divide-nhp-border">
                    {worstBucket.accounts.map((a) => (
                      <Link
                        key={a.account_id}
                        to={`/admin/pppoe-accounts/${a.account_id}`}
                        className="flex flex-col gap-1 md:grid md:grid-cols-12 px-4 py-2 text-sm hover:bg-nhp-bg"
                      >
                        <div className="font-mono md:col-span-4">{a.pppoe_username}</div>
                        <div className="font-mono md:col-span-4">{KES(a.amount_kes)}</div>
                        <div className="font-mono md:col-span-4">{a.days_overdue}d</div>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-nhp-muted">Nobody is overdue right now.</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
